/**
 * Container — centered max-width wrapper with responsive padding.
 * Generous horizontal padding for breathing room.
 */

import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

type ContainerWidth = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'full';

export interface ContainerProps extends HTMLAttributes<HTMLDivElement> {
  maxWidth?: ContainerWidth;
}

const widthMap: Record<ContainerWidth, string> = {
  sm: 'max-w-[var(--container-sm)]',
  md: 'max-w-[var(--container-md)]',
  lg: 'max-w-[var(--container-lg)]',
  xl: 'max-w-[var(--container-xl)]',
  '2xl': 'max-w-[var(--container-2xl)]',
  '3xl': 'max-w-[var(--container-3xl)]',
  '4xl': 'max-w-[var(--container-4xl)]',
  full: 'max-w-full',
};

export const Container = forwardRef<HTMLDivElement, ContainerProps>(
  ({ maxWidth = '2xl', className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('mx-auto w-full px-6 sm:px-8 lg:px-12', widthMap[maxWidth], className)}
        {...props}
      >
        {children}
      </div>
    );
  },
);

Container.displayName = 'Container';
